/**
 * Client for the server-side bulk-job runner (`POST /api/mgmt/jobs`).
 *
 * Use this instead of running a `for (const x of targets) { await api.post(...) }`
 * loop in admin pages — the loop dies if the user closes the tab. Submitting
 * a job hands the work off to a queue worker that survives.
 *
 * Pattern:
 *   const { jobPk, jobId } = await submitJob({...});
 *   const job = useJobPolling(jobPk, jobId); // polls every 2.5s, stops on terminal
 *   // or, fire-and-forget: rely on <RecentJobsPanel> to show progress
 */
import { useEffect, useRef, useState } from 'react';
import { api } from './api';

export type JobKind =
  | 'venue-scan-ig'
  | 'venue-scan-website'
  | 'venue-patch'
  | 'series-scan-ig'
  | 'venue-discover-from-events'
  | 'recalc-date'
  | 'website-source-update-promote'
  | 'website-source-capture';

export type JobStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed-with-errors'
  | 'cancelled'
  | 'failed';

export interface BulkJob {
  partitionKey: 'job';
  rowKey: string;
  kind: JobKind;
  label: string;
  status: JobStatus;
  total: number;
  done: number;
  succeeded: number;
  failed: number;
  cancelRequested?: boolean;
  paramsJson?: string;
  failuresJson?: string;
  /** JSON-encoded result blob for single-step jobs (e.g. discover-from-events). */
  resultJson?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  createdBy?: string;
}

export interface JobFailureSample {
  name: string;
  error: string;
  at: string;
}

/** Per-target payload shapes — mirror the backend `BulkJobTarget` discriminated union. */
export type JobTarget =
  | { kind: 'venue-scan-ig'; venueId: string; label: string }
  | { kind: 'venue-scan-website'; venueId: string; label: string }
  | {
      kind: 'venue-patch';
      venueId: string;
      label: string;
      patch: { active?: boolean; category?: string; categoriesJson?: string; enrichmentStatus?: string };
    }
  | { kind: 'series-scan-ig'; seriesId: string; label: string }
  | {
      kind: 'venue-discover-from-events';
      label: string;
      opts: {
        dryRun?: boolean;
        monthsBack?: number;
        minOccurrences?: number;
        skipLlm?: boolean;
        wave?: number;
        reclassifyDismissed?: boolean;
      };
    }
  | { kind: 'recalc-date'; rawPartitionKey: string; rawRowKey: string; label: string }
  | {
      kind: 'website-source-update-promote'; label: string;
      updatePartitionKey: string; candidateRowKey: string; candidateHash: string;
      expectedRawEtag: string; reviewReason: string;
    }
  | {
      kind: 'website-source-capture'; label: string; rawPartitionKey: string; rawId: string;
      expectedRawEtag: string; relatedCandidateRowKey?: string;
    };

export interface SubmitJobInput {
  kind: JobKind;
  label: string;
  targets: JobTarget[];
  paramsJson?: string;
}

export interface SubmitJobResult {
  jobPk: 'job';
  jobId: string;
  total: number;
}

export function submitJob(input: SubmitJobInput): Promise<SubmitJobResult> {
  return api.post<SubmitJobResult>('/api/mgmt/jobs', input);
}

/** Request body for the bulk recalculate-by-ingest-date tool. */
export interface BulkRecalcRequest {
  /** ISO date (YYYY-MM-DD) lower bound on ingest time. */
  fromDate?: string;
  /** ISO date (YYYY-MM-DD) upper bound on ingest time. Defaults to now. */
  toDate?: string;
  /** Shorthand window: [now - lastDays, now]. Ignored when fromDate is set. */
  lastDays?: number;
  /** Source type filter, or 'all'. */
  sourceType?: string;
  /** Only raws that produced extracted events. */
  onlyWithExtracted?: boolean;
  dryRun?: boolean;
  label?: string;
}

export interface BulkRecalcPreview {
  dryRun: true;
  fromIso: string;
  toIso: string;
  sourceType: string;
  count: number;
  overLimit: boolean;
  maxPerJob: number;
  sample: Array<{ rawRowKey: string; sourceType: string; fetchedAt: string }>;
}

export interface BulkRecalcSubmitResult {
  jobPk: 'job';
  jobId: string;
  total: number;
  fromIso: string;
  toIso: string;
  sourceType: string;
}

/** Preview (count only, no job created) the matching ingested items. */
export function previewBulkRecalculate(req: Omit<BulkRecalcRequest, 'dryRun'>): Promise<BulkRecalcPreview> {
  return api.post<BulkRecalcPreview>('/api/mgmt/events/bulk-recalculate', { ...req, dryRun: true });
}

/** Create a recalc-date bulk job for every ingested item in the window. */
export function submitBulkRecalculate(req: Omit<BulkRecalcRequest, 'dryRun'>): Promise<BulkRecalcSubmitResult> {
  return api.post<BulkRecalcSubmitResult>('/api/mgmt/events/bulk-recalculate', { ...req, dryRun: false });
}

export function getJob(pk: string, id: string): Promise<BulkJob> {
  return api.get<BulkJob>(`/api/mgmt/jobs/${encodeURIComponent(pk)}/${encodeURIComponent(id)}`);
}

export function listJobs(opts: { kind?: JobKind; status?: JobStatus; limit?: number } = {}): Promise<{ items: BulkJob[] }> {
  const sp = new URLSearchParams();
  if (opts.kind) sp.set('kind', opts.kind);
  if (opts.status) sp.set('status', opts.status);
  if (opts.limit) sp.set('limit', String(opts.limit));
  const qs = sp.toString();
  return api.get<{ items: BulkJob[] }>(`/api/mgmt/jobs${qs ? `?${qs}` : ''}`);
}

export function cancelJob(pk: string, id: string): Promise<BulkJob> {
  return api.post<BulkJob>(`/api/mgmt/jobs/${encodeURIComponent(pk)}/${encodeURIComponent(id)}/cancel`);
}

export function isTerminalStatus(s: JobStatus): boolean {
  return s === 'completed' || s === 'completed-with-errors' || s === 'cancelled' || s === 'failed';
}

export function parseFailureSamples(job: BulkJob | undefined | null): JobFailureSample[] {
  if (!job?.failuresJson) return [];
  try {
    const parsed = JSON.parse(job.failuresJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Poll a single job row at `intervalMs` cadence. Stops automatically when
 * status is terminal. Returns `{ job, error, refresh }`. Safe to leave
 * mounted; cleanup cancels the timer on unmount or when `enabled` flips.
 */
export function useJobPolling(
  pk: string | undefined,
  id: string | undefined,
  opts: { intervalMs?: number; enabled?: boolean } = {}
): { job: BulkJob | undefined; error: Error | undefined; refresh: () => void } {
  const intervalMs = Math.max(500, opts.intervalMs ?? 2500);
  const enabled = opts.enabled !== false && !!pk && !!id;
  const [job, setJob] = useState<BulkJob | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const tickRef = useRef(0);

  useEffect(() => {
    if (!enabled || !pk || !id) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      const myTick = ++tickRef.current;
      try {
        const next = await getJob(pk, id);
        if (cancelled || myTick !== tickRef.current) return;
        setJob(next);
        setError(undefined);
        if (!isTerminalStatus(next.status)) {
          timer = setTimeout(tick, intervalMs);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        // Back off to 2x interval on error so we don't hammer a flaky API.
        timer = setTimeout(tick, intervalMs * 2);
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, pk, id, intervalMs]);

  const refresh = () => {
    if (!pk || !id) return;
    void getJob(pk, id).then(setJob).catch((e) => setError(e instanceof Error ? e : new Error(String(e))));
  };

  return { job, error, refresh };
}
